<template>
  <div class="edit-profile-page">
    <main class="edit-profile-shell">
      <button class="back-btn" type="button" @click="$router.back()">
        <img class="back-icon" src="@/static/icon/left.png" alt="" />
        <span>{{ $t('common.back') }}</span>
      </button>

      <section class="profile-card">
        <header class="card-header">
          <h1>{{ $t('settings.editProfile') }}</h1>
          <p>{{ $t('settings.editProfileDesc') }}</p>
        </header>

        <div class="avatar-section">
          <button class="avatar-button" type="button" @click="triggerFileInput">
            <img :src="avatarPreview" alt="" @error="handleAvatarError" />
            <span class="avatar-overlay">{{ uploading ? $t('settings.uploading') : $t('settings.uploadAvatar') }}</span>
          </button>
          <input
            ref="fileInput"
            class="file-input"
            type="file"
            accept="image/*"
            @change="handleAvatarChange"
          />
        </div>

        <van-form class="profile-form" @submit="handleSave">
          <van-field
            v-model.trim="form.nickname"
            name="nickname"
            :label="$t('auth.nickname')"
            :placeholder="$t('auth.nicknamePlaceholder')"
            autocomplete="nickname"
            clearable
          />

          <button
            class="save-btn"
            type="submit"
            :disabled="saving || uploading"
            :class="{ disabled: saving || uploading }"
          >
            {{ saving ? $t('settings.saving') : $t('settings.save') }}
          </button>
        </van-form>
      </section>
    </main>
  </div>
</template>

<script>
import apiConfig from '@/apis/api.config.js'
import { editProfile, uploadFile } from '@/apis/user.js'
import { Toast } from 'vant'

export default {
  name: 'EditProfile',
  data() {
    return {
      defaultAvatar: require('@/static/video/davatar.png'),
      userInfo: {},
      form: {
        nickname: ''
      },
      avatar: '',
      avatarPreview: '',
      uploading: false,
      saving: false
    }
  },
  created() {
    this.initProfile()
  },
  methods: {
    initProfile() {
      const userInfo = this.$getUserInfo() || {}
      this.userInfo = userInfo
      this.form.nickname = userInfo.nickname || ''
      this.avatar = userInfo.avatar || ''
      this.avatarPreview = this.normalizeAvatarUrl(this.avatar) || this.defaultAvatar
    },
    triggerFileInput() {
      if (this.uploading) return
      this.$refs.fileInput.click()
    },
    async handleAvatarChange(event) {
      const file = event.target.files && event.target.files[0]
      event.target.value = ''

      if (!file) return

      if (!file.type || !file.type.startsWith('image/')) {
        Toast(this.$t('settings.invalidAvatar'))
        return
      }

      const previewUrl = URL.createObjectURL(file)
      this.avatarPreview = previewUrl

      const formData = new FormData()
      formData.append('file', file)

      try {
        this.uploading = true
        const response = await uploadFile(formData)
        const avatar = this.extractUploadedUrl(response)

        if (!avatar) {
          Toast(this.$t('settings.uploadFailed'))
          this.avatarPreview = this.normalizeAvatarUrl(this.avatar) || this.defaultAvatar
          return
        }

        this.avatar = avatar
        this.avatarPreview = this.normalizeAvatarUrl(avatar)
        // Toast(this.$t('settings.uploadSuccess'))
      } catch (error) {
        Toast(this.$t('settings.uploadFailed'))
        this.avatarPreview = this.normalizeAvatarUrl(this.avatar) || this.defaultAvatar
      } finally {
        this.uploading = false
        URL.revokeObjectURL(previewUrl)
      }
    },
    async handleSave() {
      if (!this.form.nickname && !this.avatar) {
        Toast(this.$t('settings.profileEmpty'))
        return
      }

      if (this.form.nickname && this.form.nickname.length < 2) {
        Toast(this.$t('settings.invalidNickname'))
        return
      }

      try {
        this.saving = true
        const response = await editProfile({
          nickname: this.form.nickname,
          avatar: this.avatar
        })
        const userinfo = response && response.data && response.data.userinfo

        if (!userinfo) {
          Toast(this.$t('settings.profileUpdateFailed'))
          return
        }

        this.$setUserInfo(userinfo)
        this.userInfo = userinfo
        this.form.nickname = userinfo.nickname || ''
        this.avatar = userinfo.avatar || ''
        this.avatarPreview = this.normalizeAvatarUrl(this.avatar) || this.defaultAvatar
        Toast(this.$t('settings.profileUpdateSuccess'))
        this.$router.back()
      } catch (error) {
        Toast(this.$t('settings.profileUpdateFailed'))
      } finally {
        this.saving = false
      }
    },
    extractUploadedUrl(response) {
      const data = response && response.data ? response.data : response
      const payload = data && data.data ? data.data : data

      if (!payload) return ''

      return payload.fullurl || payload.url || payload.avatar || payload.path || ''
    },
    normalizeAvatarUrl(url) {
      if (!url) return ''
      if (/^https?:\/\//i.test(url) || url.startsWith('blob:') || url.startsWith('data:')) {
        return url
      }

      const path = url.startsWith('/') ? url : `/${url}`
      return `${apiConfig.baseUrl}${path}`
    },
    handleAvatarError() {
      this.avatarPreview = this.defaultAvatar
    }
  }
}
</script>

<style scoped>
* {
  box-sizing: border-box;
  -webkit-tap-highlight-color: transparent;
}

.edit-profile-page {
  min-height: 100vh;
  background:
    radial-gradient(circle at 16% 10%, rgba(255, 255, 255, 0.95), transparent 28%),
    linear-gradient(145deg, #f7f8fb 0%, #eef1f5 48%, #ffffff 100%);
  color: #111111;
  font-family: -apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", sans-serif;
}

.edit-profile-shell {
  width: 100%;
  max-width: 450px;
  min-height: 100vh;
  margin: 0 auto;
  padding: 26px 18px 34px;
}

.back-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0;
  border: 0;
  background: transparent;
  color: #1d1d1f;
  font: inherit;
  font-size: 15px;
  font-weight: 500;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.profile-card {
  margin-top: 24px;
  padding: 28px 22px 24px;
  border: 1px solid rgba(255, 255, 255, 0.88);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.76);
  backdrop-filter: blur(22px);
  -webkit-backdrop-filter: blur(22px);
}

.card-header {
  text-align: center;
}

.card-header h1 {
  margin: 0;
  color: #111111;
  font-size: 30px;
  font-weight: 750;
  line-height: 1.15;
  letter-spacing: 0;
}

.card-header p {
  max-width: 280px;
  margin: 10px auto 0;
  color: #6e6e73;
  font-size: 14px;
  line-height: 1.55;
}

.avatar-section {
  display: flex;
  justify-content: center;
  margin: 26px 0 22px;
}

.avatar-button {
  position: relative;
  width: 106px;
  height: 106px;
  overflow: hidden;
  padding: 0;
  border: 4px solid rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  background: #f2f2f7;
  box-shadow: 0 12px 28px rgba(35, 42, 55, 0.13);
}

.avatar-button img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-overlay {
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 34px;
  padding: 5px 8px;
  background: rgba(17, 17, 17, 0.64);
  color: #ffffff;
  font-size: 11px;
  font-weight: 650;
  line-height: 1.2;
}

.file-input {
  display: none;
}

.profile-form {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.profile-form ::v-deep .van-cell {
  display: flex;
  align-items: center;
  min-height: 54px;
  padding: 9px 14px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.profile-form ::v-deep .van-cell::after {
  display: none;
}

.profile-form ::v-deep .van-field__label {
  display: flex;
  align-items: center;
  width: 72px;
  color: #3a3a3c;
  font-size: 14px;
  font-weight: 600;
}

.profile-form ::v-deep .van-field__body {
  display: flex;
  align-items: center;
  min-height: 34px;
}

.profile-form ::v-deep .van-field__control {
  color: #111111;
  font-size: 15px;
  line-height: 22px;
}

.profile-form ::v-deep .van-field__control::placeholder {
  color: #b0b0b7;
}

.save-btn {
  width: 100%;
  height: 52px;
  margin-top: 8px;
  border: 0;
  border-radius: 999px;
  background: #111111;
  color: #ffffff;
  font-size: 16px;
  font-weight: 700;
  box-shadow: 0 16px 30px rgba(17, 17, 17, 0.18);
}

.save-btn:active {
  transform: translateY(1px);
}

.save-btn.disabled {
  background: #c7c7cc;
  box-shadow: none;
}

@media (max-width: 380px) {
  .edit-profile-shell {
    padding-right: 14px;
    padding-left: 14px;
  }

  .profile-card {
    padding: 26px 16px 22px;
    border-radius: 24px;
  }

  .card-header h1 {
    font-size: 27px;
  }

  .profile-form ::v-deep .van-field__label {
    width: 66px;
  }
}
</style>
